import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionMessage from '@atlaskit/section-message';
import Badge from '@atlaskit/badge';
import Lozenge from '@atlaskit/lozenge';
import { SloDTO, SloGroupCohortSummaryDTO } from '@atlassiansox/phobos-client-ts';

import { KeyValue, BreachCount } from '@/components/primitive';
import {
  alignItemsStart,
  extendWidth,
  flex,
  flexCenter,
  flexDirectionColumn,
  flexOne,
  flexStart,
  spaceGapCompact,
  spaceGapCozy,
} from '@/styles';
import { SLO } from '@/ui';
import { useFeatureGatesController } from '@/controllers';
import { QualityScoreIndicator, isQualityAvailable } from '@/components/composed';

import { isIndicatorTypeMimir } from '../slo-group/utils';
import { cohortHierarchyToBFSData } from '../slo-group/slo-panel/utils';

import SLOCardHeader from './header';
import {
  getErrorBudget,
  formatPerformanceResult,
  formatObjectiveResult,
  formatErrorBudget,
  getLozengeNumberAppearance,
} from './utils';
import * as stylesLarge from './large-styles';
import * as stylesGroup from './group-card-styles';
import * as stylesMini from './mini-slo-cards/mini-styles';

type GroupCardProps = (SLO | SloDTO) & {
  isLarge?: boolean;
  isCompact?: boolean;
  isResponsive?: boolean;
  isExtending?: boolean;
};
const GroupCard: FC<GroupCardProps> = ({
  name,
  type,
  publicId,
  indicator,
  performance,
  objective,
  quality,
  isDraft,
  isCompact,
  isResponsive,
  isLarge,
  isExtending,
  sloGroupSummary,
  isSnapshot,
  cohortHierarchy,
}) => {
  const navigate = useNavigate();
  const sloGroupCohortSummaries = sloGroupSummary?.sloGroupCohortSummaries || [];

  const isMimir = isIndicatorTypeMimir(indicator);
  // Mimir SLO group doesn't support cohort hierarchy yet
  const cohortTypeSorted: string[] =
    cohortHierarchy && !isMimir
      ? cohortHierarchyToBFSData(cohortHierarchy)
      : sloGroupCohortSummaries.map(x => x.cohort);
  // For Mimir SLO groups, display all cohorts, for dynamic SLO groups, skip the
  // first cohort as it's already displayed in the main card
  const cohortTypeToDisplay: string[] = cohortTypeSorted.slice(isMimir ? 0 : 1);
  const breaches: number = sloGroupSummary?.breachesCount || 0;
  const total: number = sloGroupSummary?.count || 0;
  const hasCohortMetrics: boolean = sloGroupCohortSummaries.length > 0 && total > 0;
  // Only show cohort metrics warning for SLO group slos
  const showCohortError = !hasCohortMetrics;

  const { allowMimirSLO } = useFeatureGatesController();

  if (isMimir && !allowMimirSLO) {
    return null;
  }

  return (
    <div
      css={[
        ...(isLarge
          ? [
              stylesLarge.root,
              stylesGroup.rootLarge,
              isCompact ? stylesLarge.compactCardStyles : '',
              isResponsive ? stylesLarge.responsiveGroupCardStyles : '',
            ]
          : [stylesMini.root, stylesGroup.rootMini, isExtending ? stylesMini.extending : '']),
        isMimir ? stylesGroup.mimirStyles : '',
      ]}
    >
      {(!isCompact || !isLarge) && (
        <div css={isLarge ? '' : [stylesMini.header, extendWidth]}>
          <SLOCardHeader
            shouldTruncate={!isExtending && !isLarge}
            publicId={publicId}
            name={name}
            type={type}
            performance={performance}
            objective={objective}
            quality={quality}
            indicator={indicator}
            isDraft={isDraft}
            isAlertable={indicator && indicator.isAlertable}
            isGroup
            sloGroupSummary={sloGroupSummary}
            isCompact={isCompact}
            isSnapshot={isSnapshot}
          />
        </div>
      )}

      <div
        css={[isLarge ? stylesGroup.panelLarge : stylesGroup.panelMini, extendWidth]}
        onClick={() => {
          navigate(`/slo-${isMimir ? 'mimir' : 'group'}/${publicId}`);
        }}
      >
        <div css={[flex, alignItemsStart, flexDirectionColumn, spaceGapCozy]}>
          {!isMimir && performance && (
            <div css={[flexStart, spaceGapCozy]}>
              <Badge>
                <span css={stylesGroup.badgeStyles}>{cohortTypeSorted[0]}</span>
              </Badge>
              <KeyValue noPositionFix title="error budget">
                <Lozenge appearance="default">
                  {formatErrorBudget(getErrorBudget(performance?.result, objective))} %
                </Lozenge>
              </KeyValue>
              <KeyValue noPositionFix title="sli">
                <Lozenge appearance={getLozengeNumberAppearance({ performance, objective })}>
                  {formatPerformanceResult(performance?.result)} %
                </Lozenge>
              </KeyValue>
              {isLarge && (
                <KeyValue noPositionFix title="slo">
                  <Lozenge>{formatObjectiveResult(objective)} %</Lozenge>
                </KeyValue>
              )}
            </div>
          )}
          <div css={[flexStart, spaceGapCompact]}>
            <KeyValue noPositionFix title="slo breaching">
              <BreachCount count={breaches} />
            </KeyValue>
            <img alt="/" width={9} height={9} src="/assets/slash.svg" />
            <KeyValue noPositionFix title="total">
              <Badge max={Infinity} appearance="default">
                {total}
              </Badge>
            </KeyValue>
            {isQualityAvailable(quality) && (
              <KeyValue noPositionFix title="quality score">
                <QualityScoreIndicator quality={quality} />
              </KeyValue>
            )}
          </div>
        </div>
        {showCohortError && (
          <div css={[flexOne, extendWidth]}>
            <SectionMessage
              appearance="warning"
              testId="slo-card-cohort-metrics-error"
              title="Cohort metrics are not ready"
            >
              <></>
            </SectionMessage>
          </div>
        )}
        {!showCohortError && (
          <div css={[flexCenter, spaceGapCompact, stylesGroup.groupCardCohortsContainerStyles]}>
            {cohortTypeToDisplay.map((cohortType: string) => {
              const sloGroupCohortSummary = sloGroupCohortSummaries.find(
                x => x.cohort === cohortType
              ) as SloGroupCohortSummaryDTO;
              if (!sloGroupCohortSummary) return null;
              return (
                <div
                  key={cohortType}
                  css={[
                    flexCenter,
                    flexDirectionColumn,
                    spaceGapCompact,
                    stylesGroup.cohortBlockStyles,
                  ]}
                >
                  <Badge>
                    <span css={stylesGroup.badgeStyles}>{cohortType}</span>
                  </Badge>
                  <div css={[flexCenter, spaceGapCompact, stylesGroup.cohortBadgeStyles]}>
                    <BreachCount count={sloGroupCohortSummary.breachesCount || 0} />
                    <img alt="/" width={9} height={9} src="/assets/slash.svg" />
                    <Badge max={Infinity} appearance="default">
                      {sloGroupCohortSummary.count || '0'}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupCard;
